import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import classNames from 'classnames';
import styles from './quiz-number-rating-field.module.scss';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import { QUIZ_EVENT_TYPES, QuestionRecord, AnswerRecord } from 'src/types/Quiz';

const sortAnswers = (a: AnswerRecord, b: AnswerRecord) => {
    if (a.answerText < b.answerText) return -1;
    if (a.answerText > b.answerText) return 1;

    return 0;
};
export interface QuizNumberRatingFieldProps {
    question: string;
    explanation?: string[] | string;
    subquestions: RatingProps[];
}

const QuizNumberRatingField = () => {
    const {
        currentQuestion,
        questionList,
        questionById,
        handleSubmitAnswer,
        handleNavigateNextQuestion,
        state,
    } = useContext(QuizContext);
    // Current question
    const { medication } = currentQuestion || {};
    // State
    const [pageTransitioning] = state.pageTransitioning;
    const [continueDisabled, setContinueDisabled] = state.continueDisabled;
    // Parent question
    const parentQuestion = useMemo(() => {
        const { parentQuestion } = currentQuestion || {};
        let newCurrentQuestion = currentQuestion;
        if (parentQuestion && questionById?.[parentQuestion]) {
            newCurrentQuestion = questionById[parentQuestion];
        }

        return newCurrentQuestion;
    }, [currentQuestion, questionById]);
    const { questionId, questionText, questionExplanation } =
        parentQuestion || {};
    // Child questions
    const childQuestions = useMemo(
        () =>
            questionList.filter(
                (q) =>
                    q.parentQuestion === questionId &&
                    q.questionId !== questionId,
            ),
        [questionId, questionList],
    );

    // Handle submitting
    const handleSubmit = useCallback(() => {
        if (!continueDisabled && questionId) {
            // Submit parent
            handleSubmitAnswer(
                {
                    questionId: questionId,
                    questionText: questionText,
                    medication: medication,
                },
                () => {
                    handleNavigateNextQuestion();
                },
            );
        }
    }, [
        continueDisabled,
        questionId,
        questionText,
        medication,
        handleSubmitAnswer,
        handleNavigateNextQuestion,
    ]);

    // Enter key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter') handleSubmit();
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [handleSubmit]);
    // Continue click listener
    useEffect(() => {
        window.addEventListener(QUIZ_EVENT_TYPES.continueClick, handleSubmit);

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                handleSubmit,
            );
    }, [handleSubmit]);
    // Continue state
    useEffect(() => {
        if (!pageTransitioning) {
            if (childQuestions) {
                setContinueDisabled(
                    childQuestions.filter(
                        (q) =>
                            q.userAnswers === null ||
                            q.userAnswers.length === 0,
                    ).length > 0,
                );
            } else {
                setContinueDisabled(true);
            }
        }
    }, [pageTransitioning, childQuestions, setContinueDisabled]);

    return (
        <section className="mb-8">
            <div className={styles['quiz-number-rating--wrapper']}>
                <div className={styles['quiz-field-header']}>
                    {/* Title */}
                    <h4 className={styles['title']}>{questionText}</h4>
                    {/* Subtitle */}
                    {questionExplanation ? (
                        <ul className={classNames(styles['subtitle'], 'pt-2')}>
                            {questionExplanation
                                .split(/\r?\n/)
                                .map((text, i) => (
                                    <li key={i}>{text}</li>
                                ))}
                        </ul>
                    ) : null}
                    {/* Children */}
                    <div className={styles['quiz-rating-wrapper']}>
                        {childQuestions.map((question) => (
                            <QuizNumberRating
                                key={question.questionId}
                                question={question}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export interface RatingProps {
    question: QuestionRecord;
}
export const QuizNumberRating = ({ question }: RatingProps) => {
    const { handleSubmitAnswer, prefillQuestionIds, inQueue } =
        useContext(QuizContext);
    const {
        questionId,
        questionText,
        userAnswers,
        prefillNeeded,
        prefillAnswers,
        medication,
        answerList,
    } = question;
    const sortedAnswerList = useMemo(
        () =>
            answerList.sort(sortAnswers).map((o) => ({
                answerId: o.frontendStamp,
                answerText: o.answerText,
            })),
        [answerList],
    );

    const abortControllerRef = useRef<AbortController | null>(null);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [submitted, setSubmitted] = useState<boolean>(false);

    // Set existing answer
    useEffect(() => {
        // Loop through child questions
        const sortedAnswerList = answerList.sort(sortAnswers).map((o) => ({
            answerId: o.frontendStamp,
            answerText: o.answerText,
        }));
        // Get selected answer
        let selectedAnswerId: string | null = null;
        if (userAnswers.length > 0) {
            selectedAnswerId = userAnswers[0].answerId;
        } else if (
            inQueue &&
            prefillNeeded &&
            prefillQuestionIds.includes(questionId) &&
            prefillAnswers.length > 0
        ) {
            selectedAnswerId = prefillAnswers[0].answerId;
        }
        // Add selected answer
        if (!submitted && questionId && selectedAnswerId) {
            setSelectedIndex(
                sortedAnswerList.findIndex(
                    (a) => a.answerId === selectedAnswerId,
                ),
            );
        }
    }, [
        questionId,
        userAnswers,
        prefillNeeded,
        prefillAnswers,
        prefillQuestionIds,
        inQueue,
        submitted,
        answerList,
    ]);
    // Handle select
    const handleSelect = (selectedIndex) => {
        setSelectedIndex(selectedIndex);
    };
    // Handle submitting
    const handleSubmit = (questionId, questionText, answerId, answerText) => {
        abortControllerRef.current = new AbortController();
        const options = { signal: abortControllerRef.current.signal };
        handleSubmitAnswer(
            {
                questionId,
                questionText,
                answerIds: [answerId],
                answerTexts: [answerText],
                medication,
            },
            () => {
                setSubmitted(true);
                abortControllerRef.current = null;
            },
            options,
        );
    };
    // Handle answer click
    const handleClick = (
        questionId,
        questionText,
        answerId,
        answerText,
        selectedIndex,
    ) => {
        // If answer already submitting, abort request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        handleSelect(selectedIndex);
        handleSubmit(questionId, questionText, answerId, answerText);
    };

    if (!questionId) return null;

    return (
        <section className={classNames(styles['quiz-rating'], 'mb-8')}>
            <h4 className={styles['heading']}>{questionText}</h4>
            <div className="md:pl-6">
                <div className="flex flex-wrap gap-4">
                    {sortedAnswerList.map((a, i) => (
                        <button
                            key={a.answerId}
                            className={classNames(
                                styles['quiz_rating-button'],
                                i === selectedIndex ? styles['selected'] : '',
                            )}
                            onClick={() =>
                                handleClick(
                                    questionId,
                                    questionText,
                                    a.answerId,
                                    a.answerText,
                                    i,
                                )
                            }
                        >
                            {a.answerText}
                        </button>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default QuizNumberRatingField;
